import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Download,
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
  User,
  FileText,
} from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useAuditLogs,
  type AuditLogRow,
  type AuditLogCategory,
  type AuditLogFilters,
} from '@/hooks/useAuditLogs';
import {
  auditActionLabel,
  auditActorLabel,
  auditCategoryLabel,
  auditPurposeLabel,
  auditSourceLabel,
  auditTargetSummary,
  auditTargetTypeLabel,
  humanizeAuditToken,
} from '@/lib/auditLogLabels';
import { formatDanishTimestamp } from '@/lib/danishDate';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SectionCard } from './ClinicGeneral';
import { SettingsLoading } from './settingsChrome';

export function AuditLogs() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const canView = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  // Filters
  const [filters, setFilters] = useState<AuditLogFilters>({
    limit: 50,
    offset: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useAuditLogs(canView ? activeClinicId : null, filters);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput || undefined, offset: 0 }));
  };

  const handleFilterChange = (key: keyof AuditLogFilters, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, offset: 0 }));
  };

  const handleNextPage = () => {
    setFilters((prev) => ({ ...prev, offset: (prev.offset ?? 0) + (prev.limit ?? 50) }));
  };

  const handlePrevPage = () => {
    setFilters((prev) => ({
      ...prev,
      offset: Math.max(0, (prev.offset ?? 0) - (prev.limit ?? 50)),
    }));
  };

  const handleExport = () => {
    if (!data?.logs) return;
    
    // Convert to CSV
    const headers = [
      t('audit.csv_date'),
      t('audit.csv_category'),
      t('audit.csv_action'),
      t('audit.csv_actor'),
      t('audit.csv_target_type'),
      t('audit.csv_target'),
      t('audit.csv_source'),
      t('audit.csv_purpose'),
    ];
    const rows = data.logs.map((log) => [
      formatDanishTimestamp(log.created_at),
      auditCategoryLabel(t, log.category),
      auditActionLabel(t, log.action),
      auditActorLabel(t, log.actor_name, log.actor_email),
      auditTargetTypeLabel(t, log.target_type),
      auditTargetSummary(t, log.target_type, log.target_identifier) ?? '-',
      auditSourceLabel(t, log.source),
      auditPurposeLabel(t, log.purpose) || '-',
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canView) {
    return (
      <div className="rounded-lg border border-ds-amber-line bg-ds-amber-tint p-4">
        <p className="text-sm text-ds-amber-ink">
          {t('audit.admin_required')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <SettingsLoading />;
  }

  if (error) return <ErrorAlert message={t('audit.load_error')} />;

  const hasFilters =
    filters.category ||
    filters.action ||
    filters.actor_id ||
    filters.target_type ||
    filters.start_date ||
    filters.end_date;

  return (
    <div className="space-y-6">
      <SectionCard
        title={t('audit.title')}
        subtitle={t('audit.subtitle')}
      >
        {/* Search and Filter Bar */}
        <div className="mb-6 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('audit.search_placeholder')}
                leadingIcon={<Search className="h-4 w-4" aria-hidden />}
              />
            </div>
            <Button type="submit">
              {t('audit.search')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowFilters(!showFilters)}>
              <Filter />
              {t('audit.filters')}
              {showFilters ? <ChevronUp /> : <ChevronDown />}
            </Button>
            {data?.logs && data.logs.length > 0 && (
              <Button type="button" variant="secondary" onClick={handleExport}>
                <Download />
                {t('audit.export')}
              </Button>
            )}
          </form>

          {/* Filter Panel */}
          {showFilters && (
            <div className="grid grid-cols-1 gap-4 rounded-lg border border-ds-line bg-ds-sunk p-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Category Filter */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ds-text">
                  {t('audit.filter_category')}
                </label>
                <select
                  value={filters.category ?? ''}
                  onChange={(e) =>
                    handleFilterChange('category', e.target.value as AuditLogCategory)
                  }
                  className="w-full rounded-md border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text"
                >
                  <option value="">{t('audit.all_categories')}</option>
                  <option value="authentication">{t('audit.category_authentication')}</option>
                  <option value="data_access">{t('audit.category_data_access')}</option>
                  <option value="data_modification">{t('audit.category_data_modification')}</option>
                  <option value="cpr_access">{t('audit.category_cpr_access')}</option>
                  <option value="export">{t('audit.category_export')}</option>
                  <option value="configuration">{t('audit.category_configuration')}</option>
                  <option value="security">{t('audit.category_security')}</option>
                </select>
              </div>

              {/* Target Type Filter */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ds-text">
                  {t('audit.filter_target_type')}
                </label>
                <select
                  value={filters.target_type ?? ''}
                  onChange={(e) => handleFilterChange('target_type', e.target.value)}
                  className="w-full rounded-md border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text"
                >
                  <option value="">{t('audit.all_types')}</option>
                  <option value="patient">{t('audit.type_patient')}</option>
                  <option value="appointment">{t('audit.type_appointment')}</option>
                  <option value="invoice">{t('audit.type_invoice')}</option>
                  <option value="journal_entry">{t('audit.type_journal')}</option>
                  <option value="document">{t('audit.type_document')}</option>
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ds-text">
                  {t('audit.filter_start_date')}
                </label>
                <input
                  type="date"
                  value={filters.start_date?.split('T')[0] ?? ''}
                  onChange={(e) =>
                    handleFilterChange('start_date', e.target.value ? `${e.target.value}T00:00:00Z` : undefined)
                  }
                  className="w-full rounded-md border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="mb-1 block text-xs font-medium text-ds-text">
                  {t('audit.filter_end_date')}
                </label>
                <input
                  type="date"
                  value={filters.end_date?.split('T')[0] ?? ''}
                  onChange={(e) =>
                    handleFilterChange('end_date', e.target.value ? `${e.target.value}T23:59:59Z` : undefined)
                  }
                  className="w-full rounded-md border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text"
                />
              </div>

              {/* Clear Filters */}
              {hasFilters && (
                <div className="flex items-end md:col-span-2 lg:col-span-4">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => {
                      setFilters({ limit: 50, offset: 0 });
                      setSearchInput('');
                    }}
                  >
                    <X />
                    {t('audit.clear_filters')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        {!data?.logs || data.logs.length === 0 ? (
          <div className="rounded-md border border-dashed border-ds-line px-4 py-12 text-center text-sm text-ds-muted">
            {hasFilters || filters.search
              ? t('audit.no_results')
              : t('audit.empty')}
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-4 flex items-center justify-between text-sm text-ds-muted">
              <span>
                {t('audit.showing_results', {
                  from: (filters.offset ?? 0) + 1,
                  to: Math.min((filters.offset ?? 0) + data.logs.length, data.total_count),
                  total: data.total_count,
                })}
              </span>
            </div>

            {/* Audit Log List */}
            <div className="space-y-2">
              {data.logs.map((log) => (
                <AuditLogItem key={log.id} log={log} />
              ))}
            </div>

            {/* Pagination */}
            {(data.has_more || (filters.offset ?? 0) > 0) && (
              <div className="mt-6 flex items-center justify-between border-t border-ds-line pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handlePrevPage}
                  disabled={(filters.offset ?? 0) === 0}
                >
                  {t('audit.previous')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleNextPage}
                  disabled={!data.has_more}
                >
                  {t('audit.next')}
                </Button>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}

function AuditLogItem({ log }: { log: AuditLogRow }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const categoryColor = {
    authentication: 'bg-ds-teal-tint text-ds-teal',
    data_access: 'bg-ds-green-tint text-ds-green-ink',
    data_modification: 'bg-ds-amber-tint text-ds-amber-ink',
    cpr_access: 'bg-ds-red-tint text-ds-red-ink',
    export: 'bg-ds-teal-tint text-ds-teal',
    configuration: 'bg-ds-teal-tint text-ds-teal',
    security: 'bg-ds-red-tint text-ds-red-ink',
  }[log.category];

  const stamp = formatDanishTimestamp(log.created_at);
  const targetSummary = auditTargetSummary(t, log.target_type, log.target_identifier);

  return (
    <div className="rounded-lg border border-ds-line bg-ds-card p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-2">
          {/* Header Row */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${categoryColor}`}
            >
              {auditCategoryLabel(t, log.category)}
            </span>
            <span className="text-sm font-medium text-ds-text">
              {auditActionLabel(t, log.action)}
            </span>
            <span className="text-xs text-ds-muted">
              <Calendar className="mr-1 inline h-3 w-3" />
              {stamp}
            </span>
          </div>

          {/* Details Row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ds-muted">
            <span className="inline-flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {auditActorLabel(t, log.actor_name, log.actor_email)}
            </span>
            {targetSummary && (
              <span className="inline-flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                {targetSummary}
              </span>
            )}
            {log.purpose && (
              <span className="italic text-ds-muted">
                "{auditPurposeLabel(t, log.purpose)}"
              </span>
            )}
          </div>
        </div>

        {/* Expand Button */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="ml-4 rounded-md p-1 text-ds-muted hover:bg-ds-neutral-tint hover:text-ds-text"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 space-y-2 border-t border-ds-line pt-4">
          <DetailRow label={t('audit.detail_id')} value={log.id} />
          <DetailRow label={t('audit.detail_source')} value={auditSourceLabel(t, log.source)} />
          {log.source_function && (
            <DetailRow label={t('audit.detail_function')} value={humanizeAuditToken(log.source_function)} />
          )}
          {log.actor_role && (
            <DetailRow label={t('audit.detail_role')} value={humanizeAuditToken(log.actor_role)} />
          )}
          {log.actor_ip_address && (
            <DetailRow label={t('audit.detail_ip')} value={log.actor_ip_address} />
          )}
          {log.session_id && (
            <DetailRow label={t('audit.detail_session')} value={log.session_id} />
          )}
          {log.request_id && (
            <DetailRow label={t('audit.detail_request')} value={log.request_id} />
          )}
          {log.old_values && Object.keys(log.old_values).length > 0 && (
            <DetailRow
              label={t('audit.detail_old_values')}
              value={<pre className="text-xs">{JSON.stringify(log.old_values, null, 2)}</pre>}
            />
          )}
          {log.new_values && Object.keys(log.new_values).length > 0 && (
            <DetailRow
              label={t('audit.detail_new_values')}
              value={<pre className="text-xs">{JSON.stringify(log.new_values, null, 2)}</pre>}
            />
          )}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <DetailRow
              label={t('audit.detail_metadata')}
              value={<pre className="text-xs">{JSON.stringify(log.metadata, null, 2)}</pre>}
            />
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="text-sm">
      <span className="font-medium text-ds-text">{label}:</span>{' '}
      <span className="text-ds-muted">{value}</span>
    </div>
  );
}
